import React from "react";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getTransform(node) {
  return {
    position: {
      x: node?.transform?.position?.x ?? 0,
      y: node?.transform?.position?.y ?? 0,
      z: node?.transform?.position?.z ?? 0,
    },
    rotation: {
      x: node?.transform?.rotation?.x ?? 0,
      y: node?.transform?.rotation?.y ?? 0,
      z: node?.transform?.rotation?.z ?? 0,
    },
  };
}

export default function InstanceTransformInspector({ node, onChange }) {
  if (!node) {
    return (
      <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
        Select element to edit transform.
      </div>
    );
  }

  const isGenerated =
  node?.parentGeneratedFromId ||
  node?.panelKey ||
  node?.overrideKey;

  if (isGenerated) {
  return (
    <div className="space-y-4">
      <div className="border rounded p-4 bg-blue-50 text-sm">
        This is generated geometry.
        Transform is controlled by:
        trim / partition / bindings / snap.
      </div>

      <div className="border rounded p-3 bg-white">
  <div className="font-semibold text-sm mb-2">Resolved transform</div>

  <div className="text-xs text-gray-500">
    Position:
    X {node?.worldTransform?.position?.x ?? "-"},
    Y {node?.worldTransform?.position?.y ?? "-"},
    Z {node?.worldTransform?.position?.z ?? "-"}
  </div>

  <div className="text-xs text-gray-500 mt-1">
    Rotation:
    X {node?.worldTransform?.rotation?.x ?? "-"},
    Y {node?.worldTransform?.rotation?.y ?? "-"},
    Z {node?.worldTransform?.rotation?.z ?? "-"}
  </div>
</div>
    </div>
  );
}

  const transform = getTransform(node);

  const placement = node?.placement || {
  mode: "manual",
  face: "center",
  offset: { x: 0, y: 0, z: 0 },
};

function patchPlacement(next) {
  onChange?.({
    ...node,
    placement: {
      ...placement,
      ...next,
    },
  });
}

  function patch(group, axis, value) {
    onChange?.({
      ...node,
      transform: {
        ...transform,
        [group]: {
          ...transform[group],
          [axis]: value,
        },
      },
    });
  }

  function move(dx, dy, dz) {
    onChange?.({
      ...node,
      transform: {
        ...transform,
        position: {
          x: num(transform.position.x) + dx,
          y: num(transform.position.y) + dy,
          z: num(transform.position.z) + dz,
        },
      },
    });
  }

  function resetPosition() {
    onChange?.({
      ...node,
      transform: {
        ...transform,
        position: { x: 0, y: 0, z: 0 },
      },
    });
  }

  function resetRotation() {
    onChange?.({
      ...node,
      transform: {
        ...transform,
        rotation: { x: 0, y: 0, z: 0 },
      },
    });
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Instance Transform</h3>

<div className="border rounded p-3 bg-white space-y-3">
  <div className="text-sm font-semibold">Placement</div>

  <div>
    <label className="text-xs text-gray-500">Mode</label>
    <select
      value={placement.mode}
      onChange={(e) => patchPlacement({ mode: e.target.value })}
      className="w-full border rounded px-2 py-1 mt-1"
    >
      <option value="manual">Manual</option>
      <option value="snap">Snap to parent</option>
    </select>
  </div>

  {placement.mode === "snap" && (
    <div>
      <label className="text-xs text-gray-500">Face</label>
      <select
        value={placement.face}
        onChange={(e) => patchPlacement({ face: e.target.value })}
        className="w-full border rounded px-2 py-1 mt-1"
      >
        <option value="left">Left</option>
        <option value="right">Right</option>
        <option value="top">Top</option>
        <option value="bottom">Bottom</option>
        <option value="front">Front</option>
        <option value="back">Back</option>
        <option value="center">Center</option>
      </select>
    </div>
  )}
</div>

<div className="grid grid-cols-3 gap-2">
  {["x", "y", "z"].map((axis) => (
    <div key={axis}>
      <label className="text-xs text-gray-500">Offset {axis.toUpperCase()}</label>
      <input
  type="text"
  value={placement.offset?.[axis] ?? ""}
  onChange={(e) =>
    patchPlacement({
      offset: {
        ...(placement.offset || {}),
        [axis]: e.target.value,
      },
    })
  }
  placeholder="parent_width / 2 - 80"
/>
    </div>
  ))}
</div>

        <div className="text-xs text-gray-500">
          Move and rotate this object in the project scene.
        </div>
      </div>

      <div className="border rounded p-3 bg-gray-50 space-y-2">
        <div className="text-sm font-semibold">Quick move</div>

        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => move(-100, 0, 0)} className="px-3 py-2 border rounded bg-white">
            X -100
          </button>
          <button onClick={() => move(100, 0, 0)} className="px-3 py-2 border rounded bg-white">
            X +100
          </button>
          <button onClick={() => move(0, 0, -100)} className="px-3 py-2 border rounded bg-white">
            Z -100
          </button>
          <button onClick={() => move(0, 0, 100)} className="px-3 py-2 border rounded bg-white">
            Z +100
          </button>
          <button onClick={() => move(0, -100, 0)} className="px-3 py-2 border rounded bg-white">
            Y -100
          </button>
          <button onClick={() => move(0, 100, 0)} className="px-3 py-2 border rounded bg-white">
            Y +100
          </button>
        </div>
      </div>

      <div className="border rounded p-3 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Position</div>
          <button onClick={resetPosition} className="text-xs text-red-600">
            reset
          </button>
        </div>

        {["x", "y", "z"].map((axis) => (
          <div key={`pos_${axis}`}>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Position {axis.toUpperCase()}
            </label>
            <input
              type="text"
              value={transform.position[axis]}
              onChange={(e) => patch("position", axis, e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        ))}
      </div>

      <div className="border rounded p-3 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Rotation</div>
          <button onClick={resetRotation} className="text-xs text-red-600">
            reset
          </button>
        </div>

        {["x", "y", "z"].map((axis) => (
          <div key={`rot_${axis}`}>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Rotation {axis.toUpperCase()} degrees
            </label>
            <input
              type="text"
              value={transform.rotation[axis]}
              onChange={(e) => patch("rotation", axis, e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        ))}

        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => patch("rotation", "x", 90)} className="px-3 py-2 border rounded">
            X 90°
          </button>
          <button onClick={() => patch("rotation", "y", 90)} className="px-3 py-2 border rounded">
            Y 90°
          </button>
          <button onClick={() => patch("rotation", "z", 90)} className="px-3 py-2 border rounded">
            Z 90°
          </button>
        </div>
      </div>
    </div>
  );
}